/** Pulse tracker / retry / batch configuration (shared constants). */

/** Master kill switch — also gated by REACT_APP_PULSE_TRACKER_ENABLED. */
export const PULSE_TRACKER_ENABLED_ENV = 'REACT_APP_PULSE_TRACKER_ENABLED';
export const PULSE_COLLECTOR_URL_ENV = 'REACT_APP_PULSE_COLLECTOR_URL';

/**
 * When false, failed events are not queued and the retry watcher never runs.
 * Can also be turned off via REACT_APP_PULSE_RETRY_ENABLED=false.
 */
export const PULSE_RETRY_ENABLED = true;

export const PULSE_RETRY_ENABLED_ENV = 'REACT_APP_PULSE_RETRY_ENABLED';

/** Wait before each fail-queue retry attempt (ms): 30s → 1m → 2m. */
export const PULSE_RETRY_DELAY_30_SEC_MS = 30 * 1000;
export const PULSE_RETRY_DELAY_1_MIN_MS = 1 * 60 * 1000;
export const PULSE_RETRY_DELAY_2_MIN_MS = 2 * 60 * 1000;

export const PULSE_RETRY_DELAYS_MS = [
  PULSE_RETRY_DELAY_30_SEC_MS,
  PULSE_RETRY_DELAY_1_MIN_MS,
  PULSE_RETRY_DELAY_2_MIN_MS,
] as const;

/** Max send attempts for a fail-queued batch; queue is cleared after this regardless of outcome. */
export const PULSE_RETRY_MAX_ATTEMPTS = 3;

/** Fail-retry queue hard cap (drop oldest). Outbound uses PULSE_OUTBOUND_CAP. */
export const QUEUE_CAP = 50;

/**
 * Outbound batching (localStorage, shared across tabs).
 * Flush when event-count cap OR time cap hits first.
 */
export const PULSE_BATCH_ENABLED = true;
export const PULSE_BATCH_ENABLED_ENV = 'REACT_APP_PULSE_BATCH_ENABLED';
/** Events per POST, and the count that triggers a flush. */
export const PULSE_BATCH_MAX_EVENTS = 25;
export const PULSE_BATCH_MAX_WAIT_MS = 30 * 1000;

/**
 * Outbound storage ceiling, deliberately above PULSE_BATCH_MAX_EVENTS so a flush
 * skipped by another tab's lock cannot drop events. Only reachable if arrivals
 * outpace the drain; drops oldest first.
 */
export const PULSE_OUTBOUND_CAP = 50;

/** Multi-tab flush lock TTL. Only guards the localStorage claim, never the POST. */
export const PULSE_BATCH_FLUSH_LOCK_MS = 2 * 1000;

/** Web Locks name for the outbound claim (preferred over the localStorage lock). */
export const PULSE_OUTBOUND_LOCK_NAME = 'pulse_outbound_flush';

/**
 * Back-off after losing the flush lock to another tab. Without it a full batch
 * would reschedule itself for "now" on every miss and spin the main thread for
 * as long as the other tab is sending.
 */
export const PULSE_LOCK_RETRY_BACKOFF_MS = 250;

/** Max wait for gtag client_id / session_id before posting without them. */
export const PULSE_GA4_ID_TIMEOUT_MS = 500;

/** Debounce for the tab-activation flush handoff. */
export const PULSE_VISIBILITY_DEBOUNCE_MS = 500;

/**
 * fetch keepalive bodies are capped at 64KB by the browser; over that the request
 * is rejected outright. Retry without keepalive above this threshold.
 */
export const PULSE_KEEPALIVE_MAX_BYTES = 60 * 1024;

/** Flush tracing. Enable with REACT_APP_PULSE_DEBUG=true. */
export const PULSE_DEBUG_ENV = 'REACT_APP_PULSE_DEBUG';

/**
 * Desktop profile scrollspy: wait for scroll to settle before firing a view.
 * Avoids firing every section when jumping 1st → 4th tab (smooth scroll passes through).
 * ScrollspyNav duration is 500ms — debounce should be shorter than a deliberate pause,
 * and click suppress should cover the full animation.
 */
export const PULSE_PROFILE_SCROLL_DEBOUNCE_MS = 350;
/** Ignore scroll-driven views after a sidebar click (ms). */
export const PULSE_PROFILE_CLICK_SCROLL_SUPPRESS_MS = 600;

/**
 * Click / interaction events (ARD taps). Independent of view tracking.
 * Code switch + env (default on when unset; set REACT_APP_PULSE_CLICK_EVENTS_ENABLED=false to disable).
 * Still requires REACT_APP_PULSE_TRACKER_ENABLED=true for transport.
 */
export const PULSE_CLICK_EVENTS_ENABLED = true;
export const PULSE_CLICK_EVENTS_ENABLED_ENV = 'REACT_APP_PULSE_CLICK_EVENTS_ENABLED';
